import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { FiHome, FiCompass, FiBook, FiCreditCard, FiLogOut, FiSettings, FiUser, FiMenu, FiX, FiHelpCircle, FiSearch, FiShoppingCart } from 'react-icons/fi';
import axios from 'axios';
import Footer from '@/components/Footer';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function Layout({ children }) {
  const { user, logout, getAuthHeader } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [cartCount, setCartCount] = useState(0);

  // Non-subscribers may have a rental cart — fetch the count for the nav badge.
  const showCart = !!user && user.subscription_status !== 'active' && user.role !== 'staff';

  useEffect(() => {
    if (!showCart) {
      setCartCount(0);
      return;
    }
    let cancelled = false;
    const refresh = async () => {
      try {
        const res = await axios.get(`${API}/rentals/cart`, { headers: getAuthHeader() });
        if (!cancelled) setCartCount(res.data?.item_count || 0);
      } catch {
        if (!cancelled) setCartCount(0);
      }
    };
    refresh();
    const onUpdate = () => refresh();
    window.addEventListener('rental-cart-updated', onUpdate);
    return () => {
      cancelled = true;
      window.removeEventListener('rental-cart-updated', onUpdate);
    };
    // Refetch whenever route changes so cart count stays in sync.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showCart, location.pathname]);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/catalog?search=${encodeURIComponent(searchQuery.trim())}`);
      setSearchQuery('');
    }
  };

  const navItems = [
    { path: '/catalog', label: 'Catalog', icon: FiBook },
    { path: '/my-library', label: 'My Library', icon: FiBook },
    { path: '/faqs', label: 'FAQs', icon: FiHelpCircle },
  ];

  const handleNavClick = (path) => {
    setMobileMenuOpen(false);
    navigate(path);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-orange-50 to-yellow-50">
      {/* Decorative bookshelf background */}
      <div className="absolute inset-0 opacity-5 pointer-events-none">
        <svg className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <pattern id="bookshelf-pattern" x="0" y="0" width="200" height="200" patternUnits="userSpaceOnUse">
              <rect x="10" y="40" width="30" height="60" fill="currentColor" />
              <rect x="45" y="30" width="25" height="70" fill="currentColor" />
              <rect x="75" y="45" width="35" height="55" fill="currentColor" />
              <rect x="115" y="35" width="28" height="65" fill="currentColor" />
              <rect x="148" y="50" width="32" height="50" fill="currentColor" />
              <line x1="0" y1="100" x2="200" y2="100" stroke="currentColor" strokeWidth="2" />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#bookshelf-pattern)" />
        </svg>
      </div>

      {/* Navigation */}
      <nav className="relative z-20 bg-white/80 backdrop-blur-md border-b border-gray-200 shadow-sm">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between h-16">
            {/* Logo and Desktop Nav */}
            <div className="flex items-center space-x-8">
              <Link to="/" className="flex items-center space-x-3">
                <img 
                  src="/library-logo.png" 
                  alt="The Hyd Library" 
                  className="h-10 md:h-12 w-auto"
                />
              </Link>
              
              {/* Desktop Navigation */}
              <div className="hidden md:flex space-x-1">
                {navItems.map((item) => {
                  const Icon = item.icon;
                  const isActive = location.pathname === item.path;
                  return (
                    <Link
                      key={item.path}
                      to={item.path}
                      className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors ${
                        isActive
                          ? 'bg-amber-100 text-amber-700 font-medium'
                          : 'text-gray-600 hover:bg-gray-100'
                      }`}
                      data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
                    >
                      <Icon className="w-4 h-4" />
                      <span>{item.label}</span>
                    </Link>
                  );
                })}
              </div>
            </div>

            {/* Search Bar - Desktop */}
            <div className="hidden md:flex flex-1 max-w-md mx-4">
              <form onSubmit={handleSearch} className="w-full">
                <div className="relative">
                  <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search books..."
                    className="w-full pl-10 pr-4 py-2 bg-gray-100 border border-transparent rounded-full text-sm focus:outline-none focus:bg-white focus:border-amber-500 focus:ring-2 focus:ring-amber-200 transition-all"
                    data-testid="header-search-input"
                  />
                </div>
              </form>
            </div>

            {/* Desktop Right Section */}
            <div className="hidden md:flex items-center space-x-4">
              <Link
                to="/profile"
                className="flex items-center space-x-2 text-sm text-gray-600 hover:text-amber-600 transition-colors"
                data-testid="profile-link"
              >
                <FiUser className="w-4 h-4" />
                <span className="font-medium">{user?.name}</span>
                {user?.role === 'staff' && (
                  <span className="ml-1 px-2 py-0.5 bg-blue-100 text-blue-700 rounded text-xs">Staff</span>
                )}
              </Link>
              
              {user?.role === 'staff' && (
                <Link
                  to="/admin"
                  className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                  data-testid="admin-dashboard-link"
                >
                  <FiSettings className="w-5 h-5" />
                </Link>
              )}
              
              {showCart && (
                <Link
                  to="/rental-cart"
                  className="relative p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                  data-testid="cart-nav-link"
                  aria-label="Rental cart"
                >
                  <FiShoppingCart className="w-5 h-5" />
                  {cartCount > 0 && (
                    <span className="absolute -top-1 -right-1 w-5 h-5 bg-amber-600 text-white text-[10px] font-bold rounded-full flex items-center justify-center" data-testid="cart-count">
                      {cartCount}
                    </span>
                  )}
                </Link>
              )}
              
              <button
                onClick={handleLogout}
                className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                data-testid="logout-button"
              >
                <FiLogOut className="w-5 h-5" />
              </button>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              data-testid="mobile-menu-button"
            >
              {mobileMenuOpen ? <FiX className="w-6 h-6" /> : <FiMenu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-white border-t border-gray-200 shadow-lg">
            <div className="px-4 py-3 space-y-1">
              {/* Mobile Search Bar */}
              <form onSubmit={handleSearch} className="mb-3">
                <div className="relative">
                  <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search books..."
                    className="w-full pl-10 pr-4 py-3 bg-gray-100 border border-transparent rounded-lg text-sm focus:outline-none focus:bg-white focus:border-amber-500 focus:ring-2 focus:ring-amber-200 transition-all"
                    data-testid="mobile-search-input"
                  />
                </div>
              </form>
              
              {navItems.map((item) => {
                const Icon = item.icon;
                const isActive = location.pathname === item.path;
                return (
                  <button
                    key={item.path}
                    onClick={() => handleNavClick(item.path)}
                    className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors ${
                      isActive
                        ? 'bg-amber-100 text-amber-700 font-medium'
                        : 'text-gray-600 hover:bg-gray-100'
                    }`}
                    data-testid={`mobile-nav-${item.label.toLowerCase().replace(' ', '-')}`}
                  >
                    <Icon className="w-5 h-5" />
                    <span>{item.label}</span>
                  </button>
                );
              })}
              
              {/* Divider */}
              <div className="border-t border-gray-200 my-2"></div>
              
              {/* Profile */}
              <button
                onClick={() => handleNavClick('/profile')}
                className="w-full flex items-center space-x-3 px-4 py-3 rounded-lg text-gray-600 hover:bg-gray-100 transition-colors"
              >
                <FiUser className="w-5 h-5" />
                <span>Profile</span>
              </button>
              
              {/* Admin (if staff) */}
              {user?.role === 'staff' && (
                <button
                  onClick={() => handleNavClick('/admin')}
                  className="w-full flex items-center space-x-3 px-4 py-3 rounded-lg text-gray-600 hover:bg-gray-100 transition-colors"
                >
                  <FiSettings className="w-5 h-5" />
                  <span>Admin Dashboard</span>
                </button>
              )}
              
              {/* Logout */}
              <button
                onClick={() => {
                  setMobileMenuOpen(false);
                  handleLogout();
                }}
                className="w-full flex items-center space-x-3 px-4 py-3 rounded-lg text-red-600 hover:bg-red-50 transition-colors"
              >
                <FiLogOut className="w-5 h-5" />
                <span>Logout</span>
              </button>
            </div>
          </div>
        )}
      </nav>

      {/* Main Content */}
      <main className="relative z-10 min-h-[calc(100vh-200px)]">
        {children}
      </main>

      {/* Footer */}
      <Footer />
    </div>
  );
}

export default Layout;